'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useEventBookmarkAction } from '@/hooks';
import { useHistoricalChartData } from '@/hooks/MarketDetails/useHistoricalChartData';
import type { EventResponse } from '@/types';
import { filterAndSortMarkets, formatVolume, getEventType } from '@/utils';

import { DEFAULT_SELECTED_RANGE, MarketTimeRange, ROUTES } from '@/constants';

const LINE_COLORS = ['#2563eb', '#22c55e', '#f59e0b', '#a855f7', '#ec4899'];
const RANGES: MarketTimeRange[] = ['1D', '1W', '1M', 'ALL'];

const parseMaybe = (v: unknown): unknown => {
  if (typeof v === 'string') {
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  }
  return v;
};

/**
 * The first-outcome ("Yes") probability for a market, read robustly across the
 * feed's shapes: outcomePrices (JSON array) → outcome objects → bestBid/lastTrade.
 * getMarketProbability alone returns 0 when `outcomes` is an array of names.
 */
const getYesPrice = (m: any): number => {
  if (!m) return 0;
  const prices = parseMaybe(m.outcomePrices);
  if (Array.isArray(prices) && prices.length) {
    const p = Number(prices[0]);
    if (Number.isFinite(p) && p > 0) return p;
  }
  const outs = parseMaybe(m.outcomes);
  if (Array.isArray(outs) && outs.length && typeof outs[0] === 'object' && outs[0]) {
    const p = Number((outs[0] as any).price ?? (outs[0] as any).midpoint);
    if (Number.isFinite(p) && p > 0) return p;
  }
  const bb = Number(m.bestBid ?? m.lastTradePrice);
  return Number.isFinite(bb) ? bb : 0;
};

interface FeaturedMarketSlideProps {
  event: EventResponse;
  /** Fired once when this market settles with no usable chart data (so the carousel can skip it). */
  onNoData?: () => void;
}

export const FeaturedMarketSlide = ({ event, onNoData }: FeaturedMarketSlideProps) => {
  const theme = useTheme();
  const [range, setRange] = useState<MarketTimeRange>(DEFAULT_SELECTED_RANGE);
  const { checkIsBookmarked, handleBookmarkToggle } = useEventBookmarkAction();

  // The list feed already carries clobTokenIds + outcome prices, so we render
  // straight from it (no extra per-slide detail fetch — that endpoint is flaky).
  // Prefer live (non-closed) sub-markets so grouped events don't chart resolved legs.
  const sortedMarkets = useMemo(() => {
    const all = event.markets || [];
    const live = all.filter((m: any) => m?.closed !== true && m?.active !== false);
    return filterAndSortMarkets(live.length ? live : all);
  }, [event.markets]);
  const eventType = getEventType(event);
  const isGrouped =
    (eventType === 'MULTI_OUTCOME' || eventType === 'MULTI_QUESTION') && sortedMarkets.length > 1;

  // Which markets feed the chart (max 3, as the shared hook supports).
  const chartMarkets = useMemo(() => sortedMarkets.slice(0, 3), [sortedMarkets]);
  const { historicalData, isChartLoading } = useHistoricalChartData(chartMarkets, range);

  // Outcomes shown in the left list — probability read from outcomePrices (robust
  // across the feed's shapes; getMarketProbability returns 0 for name-only outcomes).
  const outcomes = useMemo(() => {
    if (isGrouped) {
      return sortedMarkets.slice(0, 4).map((m, i) => ({
        name: (m as any).groupItemTitle || m.question || `Outcome ${i + 1}`,
        prob: getYesPrice(m),
        color: LINE_COLORS[i % LINE_COLORS.length],
      }));
    }
    const yes = getYesPrice(sortedMarkets[0]);
    return [
      { name: 'Yes', prob: yes, color: '#22c55e' },
      { name: 'No', prob: Math.max(0, 1 - yes), color: '#ef4444' },
    ];
  }, [isGrouped, sortedMarkets]);

  // Legend for the chart lines (grouped → each market; binary → single "Yes" line).
  const chartLines = useMemo(() => {
    if (isGrouped) {
      return chartMarkets.map((m, i) => ({
        key: `price${i}`,
        name: (m as any).groupItemTitle || m.question || `Outcome ${i + 1}`,
        color: LINE_COLORS[i % LINE_COLORS.length],
      }));
    }
    return [{ key: 'price0', name: outcomes[0]?.name || 'Yes', color: '#22c55e' }];
  }, [isGrouped, chartMarkets, outcomes]);

  // Some outcome tokens have no price history upstream — only keep lines that
  // actually have data so the legend always matches what's drawn on the chart.
  const activeLines = useMemo(() => {
    if (!historicalData.length) return chartLines;
    const withData = chartLines.filter((l) =>
      historicalData.some((pt: any) => pt[l.key] !== null && pt[l.key] !== undefined)
    );
    return withData.length ? withData : chartLines;
  }, [chartLines, historicalData]);

  const marketHref = ROUTES.MARKET_DETAILS(event.slug || event.markets?.[0]?.id || '');
  const image = event.image;
  const volume = event.volume ?? 0;
  const endDate = sortedMarkets[0]?.endDate;
  const isBookmarked = checkIsBookmarked(event.id);

  // Once the chart has settled with no usable data, ask the carousel to skip it.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (!reportedRef.current && !isChartLoading && historicalData.length < 2) {
      reportedRef.current = true;
      onNoData?.();
    }
  }, [isChartLoading, historicalData.length, onNoData]);

  const fmtDate = (t: number) => {
    const d = new Date(t < 1e12 ? t * 1000 : t);
    if (range === '1D') return d.toLocaleTimeString('en-US', { hour: 'numeric' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' },
        gap: { xs: 2, md: 3 },
      }}
    >
      {/* Left: header + outcomes */}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          {image ? (
            <Box
              component="img"
              src={image}
              alt=""
              sx={{ width: 48, height: 48, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'action.hover', flexShrink: 0 }} />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              component={Link}
              href={marketHref}
              sx={{
                display: 'block',
                fontWeight: 800,
                fontSize: { xs: '1.05rem', md: '1.25rem' },
                color: 'text.primary',
                textDecoration: 'none',
                lineHeight: 1.25,
                '&:hover': { color: 'primary.main' },
              }}
            >
              {event.title}
            </Typography>
          </Box>
          <Box
            component="button"
            aria-label="Bookmark"
            onClick={(e: any) => handleBookmarkToggle(e, event, 'market')}
            sx={{
              border: 'none',
              bgcolor: 'transparent',
              cursor: 'pointer',
              color: isBookmarked ? 'primary.main' : 'text.secondary',
              p: 0.5,
              '&:hover': { color: 'primary.main' },
            }}
          >
            {isBookmarked ? (
              <BookmarkIcon sx={{ fontSize: 20 }} />
            ) : (
              <BookmarkBorderIcon sx={{ fontSize: 20 }} />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {outcomes.map((o) => (
            <Box
              key={o.name}
              component={Link}
              href={marketHref}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                py: 1,
                px: 1,
                borderRadius: 1.5,
                textDecoration: 'none',
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: o.color, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ flex: 1, color: 'text.primary', fontWeight: 600 }} noWrap>
                {o.name}
              </Typography>
              <Typography sx={{ fontWeight: 800, color: 'text.primary' }}>
                {Math.round(o.prob * 100)}%
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 2, color: 'text.secondary' }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {formatVolume(volume)} Vol.
          </Typography>
          {endDate && (
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Ends {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Right: live chart */}
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {activeLines.map((l) => (
              <Box key={l.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: l.color }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }} noWrap>
                  {l.name}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderRadius: 2, bgcolor: 'action.hover' }}>
            {RANGES.map((r) => (
              <Box
                key={r}
                component="button"
                onClick={() => setRange(r)}
                sx={{
                  cursor: 'pointer',
                  border: 'none',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1.5,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  color: range === r ? 'primary.main' : 'text.secondary',
                  bgcolor: range === r ? 'background.paper' : 'transparent',
                }}
              >
                {r}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ height: { xs: 200, md: 240 }, width: '100%' }}>
          {isChartLoading ? (
            <Skeleton variant="rounded" width="100%" height="100%" />
          ) : historicalData.length < 2 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                Not enough chart data yet
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historicalData} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={fmtDate}
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={44}
                />
                <YAxis
                  width={40}
                  domain={[0, 1]}
                  tickFormatter={(v) => `${Math.round(v * 100)}%`}
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {fmtDate(Number(label))}
                        </Typography>
                        {payload.map((p) => {
                          const line = chartLines.find((l) => l.key === p.dataKey);
                          if (!line) return null;
                          return (
                            <Typography
                              key={String(p.dataKey)}
                              variant="body2"
                              sx={{ fontWeight: 700, color: line.color }}
                            >
                              {line.name}: {Math.round(Number(p.value) * 100)}%
                            </Typography>
                          );
                        })}
                      </Box>
                    );
                  }}
                />
                {activeLines.map((l) => (
                  <Line
                    key={l.key}
                    type="monotone"
                    dataKey={l.key}
                    stroke={l.color}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
};
